/**
 * Groups Over/Under market values by line (1.5, 2.5, etc.) so each line is one row with two buttons.
 * Also groups Handicap values by line (marketId → line) so each line is one row with Home/Draw/Away.
 * Also exports mergeOverUnderByFamily for merging O/U markets across chip families.
 */

import {
  HANDICAP_MARKET_ID_TO_LINE,
  HANDICAP_MARKET_FAMILY,
  OU_MARKET_FAMILIES,
  MARKET_IDS,
} from '../../../src/create/constants/matchDetails.constants';
import type { MarketOddValue, MarketOdds } from '../../../src/create/types/create.types';

/** Parse line number from value (handles "1.5" and "1,5"). */
function getLineFromValue(value: string): number | null {
  const v = String(value ?? '').replace(/,/g, '.');
  const m = v.match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : null;
}

export interface OverUnderRow {
  lineLabel: string;
  values: MarketOddValue[];
}

/** Collapse to exactly one Over and one Under per line (handles duplicate or multi-option API data). */
function collapseToOneOverOneUnder(vals: MarketOddValue[]): MarketOddValue[] {
  const over = vals.find((x) => String(x.value ?? '').toLowerCase().includes('over'));
  const under = vals.find((x) => String(x.value ?? '').toLowerCase().includes('under'));
  const out: MarketOddValue[] = [];
  if (over) out.push(over);
  if (under) out.push(under);
  return out;
}

/**
 * Group Over/Under values by line. Each row has exactly two options (Over X.5, Under X.5).
 * Collapses to one Over + one Under per line so we never show 6 cells per row.
 */
export function groupOverUnderValuesByLine(values: MarketOddValue[]): OverUnderRow[] {
  if (!values?.length) return [];
  const byLine = new Map<number, MarketOddValue[]>();
  for (const v of values) {
    const line = getLineFromValue(v.value ?? '');
    if (line == null) continue;
    const list = byLine.get(line) || [];
    list.push(v);
    byLine.set(line, list);
  }
  const rows: OverUnderRow[] = [];
  const sortedLines = Array.from(byLine.keys())
    .filter((line) => line % 1 !== 0)
    .sort((a, b) => a - b);
  for (const line of sortedLines) {
    const vals = byLine.get(line) || [];
    const collapsed = collapseToOneOverOneUnder(vals);
    if (collapsed.length === 0) continue;
    const ordered = [...collapsed].sort((a, b) => {
      const aOver = String(a.value ?? '').toLowerCase().includes('over');
      const bOver = String(b.value ?? '').toLowerCase().includes('over');
      if (aOver && !bOver) return -1;
      if (!aOver && bOver) return 1;
      return 0;
    });
    rows.push({ lineLabel: String(line), values: ordered });
  }
  return rows;
}

/**
 * Group Handicap values by line (using marketId → line). Each row is one handicap line with Home, Draw, Away.
 * Values must have marketId set (merged handicap market from MatchMarketsPicker).
 */
export function groupHandicapValuesByLine(values: MarketOddValue[]): OverUnderRow[] {
  if (!values?.length) return [];
  const byMarketId = new Map<number, MarketOddValue[]>();
  for (const v of values) {
    const mid = v.marketId;
    if (mid == null || HANDICAP_MARKET_ID_TO_LINE[mid] === undefined) continue;
    const list = byMarketId.get(mid) || [];
    list.push(v);
    byMarketId.set(mid, list);
  }
  const order: ('Home' | 'Draw' | 'Away')[] = ['Home', 'Draw', 'Away'];
  const sortedMarketIds = Array.from(byMarketId.keys()).sort(
    (a, b) => (HANDICAP_MARKET_ID_TO_LINE[a] ?? 0) - (HANDICAP_MARKET_ID_TO_LINE[b] ?? 0)
  );
  const rows: OverUnderRow[] = [];
  for (const marketId of sortedMarketIds) {
    const line = HANDICAP_MARKET_ID_TO_LINE[marketId];
    if (line === 0) continue; // same as 1X2; hide to avoid confusion
    const vals = byMarketId.get(marketId) || [];
    const ordered = order
      .map((outcome) => vals.find((v) => String(v.value).trim().toLowerCase() === outcome.toLowerCase()))
      .filter((v): v is MarketOddValue => v != null);
    if (ordered.length !== 3) continue;
    const lineLabel = line >= 0 ? `+${line}` : String(line);
    rows.push({ lineLabel, values: ordered });
  }
  return rows;
}

/**
 * Group legacy Handicap Result (market id 9) values by line. Values are like "Home -1", "Draw -2", "Away 0".
 * Parses line from value and groups into rows with Home, Draw, Away per line.
 */
export function groupLegacyHandicapResultValuesByLine(values: MarketOddValue[]): OverUnderRow[] {
  if (!values?.length) return [];
  const byLine = new Map<number, MarketOddValue[]>();
  const lineFromValue = (val: string): number | null => {
    const m = String(val ?? '').trim().match(/([+-]?\d+)/);
    return m ? parseInt(m[1], 10) : null;
  };
  const outcomeFromValue = (val: string): 'Home' | 'Draw' | 'Away' | null => {
    const lower = String(val ?? '').trim().toLowerCase();
    if (/^home\b|home\s+[+-]?\d/.test(lower) && !/draw|away/.test(lower)) return 'Home';
    if (/^away\b|away\s+[+-]?\d/.test(lower) && !/draw/.test(lower)) return 'Away';
    if (/^draw\b|draw\s+[+-]?\d|x\b/.test(lower)) return 'Draw';
    return null;
  };
  for (const v of values) {
    const line = lineFromValue(v.value ?? '');
    const outcome = outcomeFromValue(v.value ?? '');
    if (line === null || outcome === null) continue;
    const list = byLine.get(line) || [];
    list.push(v);
    byLine.set(line, list);
  }
  const order: ('Home' | 'Draw' | 'Away')[] = ['Home', 'Draw', 'Away'];
  const sortedLines = Array.from(byLine.keys()).sort((a, b) => a - b);
  const rows: OverUnderRow[] = [];
  for (const line of sortedLines) {
    if (line === 0) continue; // Line 0 is no handicap; hide to avoid confusion with 1X2
    const vals = byLine.get(line) || [];
    const ordered = order
      .map((outcome) => vals.find((v) => outcomeFromValue(v.value ?? '') === outcome))
      .filter((v): v is MarketOddValue => v != null);
    if (ordered.length !== 3) continue;
    const lineLabel = line >= 0 ? `+${line}` : String(line);
    rows.push({ lineLabel, values: ordered });
  }
  return rows;
}

export function parseHandicapLineLabel(lineLabel: string): number | null {
  if (!lineLabel) return null;
  const parsed = parseFloat(lineLabel);
  return Number.isNaN(parsed) ? null : parsed;
}

export function getHandicapOptionDisplayLabel(line: number, outcome: 'Home' | 'Draw' | 'Away' | string): string {
  if (line === 0) return `${outcome} 0`;
  const displayLine = outcome.toLowerCase() === 'away' ? -line : line;
  const sign = displayLine > 0 ? '+' : '';
  return `${outcome} ${sign}${displayLine}`;
}

// ── mergeOverUnderByFamily ────────────────────────────────────────────────────

const OU_LINE_REGEX = /(-?\d+\.?\d*)\s*$/;

function parseLineFromName(marketName: string): string | null {
  const m = String(marketName ?? '').trim().match(OU_LINE_REGEX);
  return m ? m[1] : null;
}

const MAIN_BEFORE_HANDICAP_IDS = [MARKET_IDS.MATCH_WINNER, MARKET_IDS.DOUBLE_CHANCE, MARKET_IDS.BTTS];

/** Merge O/U markets by family (1.5, 2.5, 3.5 in one table). Same behavior for create flow and modal. */
export function mergeOverUnderByFamily(markets: MarketOdds[]): MarketOdds[] {
  const byFamily = new Map<(typeof OU_MARKET_FAMILIES)[number], MarketOdds[]>();
  const handicapMarkets: MarketOdds[] = [];
  const nonOu: MarketOdds[] = [];
  for (const m of markets) {
    const family = OU_MARKET_FAMILIES.find((f) => f.ids.includes(m.id)) ?? null;
    if (family && (m.values?.length ?? 0) > 0 && (family.ids.length === 1 || parseLineFromName(m.name))) {
      const list = byFamily.get(family) ?? [];
      list.push(m);
      byFamily.set(family, list);
    } else if (HANDICAP_MARKET_FAMILY.ids.includes(m.id) && (m.values?.length ?? 0) > 0) {
      handicapMarkets.push(m);
    } else {
      nonOu.push(m);
    }
  }
  const ouSections: MarketOdds[] = [];
  for (const family of OU_MARKET_FAMILIES) {
    const list = byFamily.get(family);
    if (!list?.length) continue;
    const sorted = [...list].sort(
      (a, b) =>
        (parseFloat(parseLineFromName(a.name) ?? '0') || 0) -
        (parseFloat(parseLineFromName(b.name) ?? '0') || 0)
    );
    const values: Array<MarketOddValue> = [];
    for (const m of sorted) {
      const line = parseLineFromName(m.name);
      for (const v of m.values ?? []) {
        const val = String(v.value ?? '').trim();
        const hasNum = /\d/.test(val);
        const isOver = val.toLowerCase().includes('over');
        const valueStr = line ? (isOver ? `Over ${line}` : `Under ${line}`) : val;
        if (!line && !hasNum) continue;
        values.push({
          value: hasNum ? val : valueStr,
          odd: Number(v.odd) || 0,
          marketId: m.id,
        });
      }
    }
    ouSections.push({ id: family.ids[0], name: family.baseName, values });
  }
  let handicapSection: MarketOdds | null = null;
  if (handicapMarkets.length > 0) {
    const sorted = [...handicapMarkets].sort(
      (a, b) =>
        (parseFloat(parseLineFromName(a.name) ?? '0') || 0) -
        (parseFloat(parseLineFromName(b.name) ?? '0') || 0)
    );
    const values: Array<MarketOddValue> = [];
    for (const m of sorted) {
      for (const v of m.values ?? []) {
        values.push({
          value: String(v.value ?? '').trim(),
          odd: Number(v.odd) || 0,
          marketId: v.marketId ?? m.id,
        });
      }
    }
    const handicapName =
      handicapMarkets.length === 1 && sorted[0].name
        ? sorted[0].name
        : HANDICAP_MARKET_FAMILY.baseName;
    handicapSection = {
      id: HANDICAP_MARKET_FAMILY.ids[0],
      name: handicapName,
      values,
    };
  }
  const beforeHandicap = nonOu.filter((m) =>
    (MAIN_BEFORE_HANDICAP_IDS as number[]).includes(m.id)
  );
  const afterHandicap = nonOu.filter((m) =>
    !(MAIN_BEFORE_HANDICAP_IDS as number[]).includes(m.id)
  );
  return [
    ...beforeHandicap,
    ...(handicapSection ? [handicapSection] : []),
    ...afterHandicap,
    ...ouSections,
  ];
}
